import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import MenuItemTile from "@/layouts/Menu/components/MenuItemTile/MenuItemTile";
import NavigationHeader from "@/layouts/Menu/components/NavigationHeader/NavigationHeader";
import DishFilterBar from "@/components/DishFilterBar/DishFilterBar";
import MenuTypeDrawer from "@/components/MenuTypeDrawer/MenuTypeDrawer";
import { useMenuData } from "@/hooks/useMenuData";
import { useDishReference } from "@/hooks/useDishReference";
import { useDishFilters } from "@/hooks/useDishFilters";
import {
  getTranslation,
  getTranslatedDescription,
} from "@/helpers/getTranslation";
import { cartItemCount, formatGrosze } from "@/helpers/cartPricing";
import { useCartLines, useCartSubtotal } from "@/store/useCartStore";
import { ALL_MENU_TYPE_ID, billsPath, orderPaths } from "@/routes/paths";
import { useGoBack } from "@/hooks/useGoBack";
import OrderActionBar from "./components/OrderActionBar";
import styles from "./Order.module.scss";

/** The dishes in one menu, split into category tabs. */
function OrderMenuTypePage() {
  const {
    restaurantId = "",
    tableId = "",
    menuTypeId = "",
  } = useParams<{
    restaurantId: string;
    tableId: string;
    menuTypeId: string;
  }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();
  const paths = orderPaths(restaurantId, tableId);

  const { dishes, groups, menuTypeGroups, loading, error } =
    useMenuData(restaurantId);
  const lines = useCartLines();
  const subtotal = useCartSubtotal();
  const itemCount = cartItemCount(lines);

  const showAll = menuTypeId === ALL_MENU_TYPE_ID;
  const typeGroup = menuTypeGroups.find(
    (candidate) => candidate.menuType.id === menuTypeId,
  );
  const menuDishes = showAll ? dishes : (typeGroup?.dishes ?? []);
  const categoryGroups = showAll ? groups : (typeGroup?.categories ?? []);
  const title = showAll
    ? t("order.allDishes")
    : (typeGroup?.name ?? t("order.title"));

  // With no menu to pick, this screen is the front page and back leaves the
  // ordering flow entirely — see the redirect in OrderMenuTypesPage.
  const goBack = useGoBack(
    menuTypeGroups.length > 0 ? paths.root : billsPath(restaurantId, tableId),
  );

  /** Index 0 is every dish in this menu; the rest are its categories. */
  const [selectedTab, setSelectedTab] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  // Switching menus reuses this component, so the tab has to be reset by hand.
  useEffect(() => {
    setSelectedTab(0);
  }, [menuTypeId]);

  const selectedCategory = categoryGroups[selectedTab - 1];
  const scopedDishes = selectedCategory ? selectedCategory.dishes : menuDishes;

  const reference = useDishReference(restaurantId);
  /**
   * Inside one menu the menu type filter can only take dishes away, so it is
   * dropped: useDishFilters hides a group with no options. Under "all dishes"
   * it is the one place the guest can still narrow by menu.
   */
  const scopedReference = useMemo(
    () => (showAll ? reference : { ...reference, menuTypes: [] }),
    [reference, showAll],
  );
  const filters = useDishFilters(scopedDishes, scopedReference);
  const visibleDishes = filters.visibleDishes;

  const menuOptions = useMemo(
    () => [
      {
        id: ALL_MENU_TYPE_ID,
        name: t("order.allDishes"),
        subtitle: t("order.dishCount", { count: dishes.length }),
      },
      ...menuTypeGroups.map((group) => ({
        id: group.menuType.id,
        name: group.name,
        subtitle: t("order.dishCount", { count: group.dishes.length }),
      })),
    ],
    [dishes.length, menuTypeGroups, t],
  );

  /** Units of each dish already in the cart, across all its option variants. */
  const countByDishId = useMemo(() => {
    const counts = new Map<string, number>();
    lines.forEach((line) => {
      counts.set(line.dishId, (counts.get(line.dishId) ?? 0) + line.quantity);
    });
    return counts;
  }, [lines]);

  return (
    <>
      <Grid>
        <div className={`start-1 col-4 ${styles.orderWrapper}`}>
          <div className={styles.orderHeaderContainer}>
            <div className={styles.orderHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={goBack}
                color="primary"
                variant="text"
              />
              {menuTypeGroups.length > 0 ? (
                <div
                  className={styles.orderTitleButton}
                  onClick={() => setMenuOpen(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setMenuOpen(true);
                    }
                  }}
                >
                  <p className={`body-bold-large ${styles.orderTitle}`}>
                    {title}
                  </p>
                  <i
                    className={`las la-angle-down icon-medium ${styles.orderTitleChevron}`}
                  />
                </div>
              ) : (
                <p className={`body-bold-large ${styles.orderTitle}`}>{title}</p>
              )}
              <div className={styles.cartButtonWrapper}>
                <IconButton
                  iconName="las la-shopping-bag icon-big"
                  onClick={() => navigate(paths.cart)}
                  color="primary"
                  variant="text"
                />
                {itemCount > 0 ? (
                  <div className={styles.cartButtonBadge}>
                    <p
                      className={`body-bold-small ${styles.cartButtonBadgeValue}`}
                    >
                      {itemCount}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>

            {categoryGroups.length > 0 ? (
              <div className={styles.orderTabs}>
                <NavigationHeader
                  navigationHeaders={[
                    { label: t("order.all"), action: () => {} },
                    ...categoryGroups.map((group) => ({
                      label: group.categoryName,
                      action: () => {},
                    })),
                  ]}
                  selectedTab={selectedTab}
                  onTabChange={setSelectedTab}
                />
              </div>
            ) : null}
          </div>

          <div className={styles.orderContent}>
            {!showAll && !typeGroup && !loading ? (
              <p className={`body-medium ${styles.orderStateMessage}`}>
                {error ? t("order.loadError") : t("order.emptyMenu")}
              </p>
            ) : null}

            <DishFilterBar filters={filters} />

            {filters.hasActiveFilters && visibleDishes.length === 0 ? (
              <p className={`body-medium ${styles.orderStateMessage}`}>
                {t("order.filters.noResults")}
              </p>
            ) : null}

            <div className={styles.orderSection}>
              {visibleDishes.map((dish) => (
                <MenuItemTile
                  key={dish.id}
                  name={getTranslation(dish.translations, language)}
                  description={getTranslatedDescription(
                    dish.translations,
                    language,
                  )}
                  price={(dish.price / 100).toFixed(2)}
                  dishImage={dish.image?.url}
                  count={countByDishId.get(dish.id) ?? 0}
                  onClick={() => navigate(paths.dish(dish.id))}
                />
              ))}
            </div>
          </div>
        </div>
      </Grid>

      <MenuTypeDrawer
        open={menuOpen}
        title={t("order.selectMenu")}
        options={menuOptions}
        onSelect={(id) => {
          setMenuOpen(false);
          if (id !== menuTypeId) navigate(paths.menuType(id), { replace: true });
        }}
        onClose={() => setMenuOpen(false)}
      />

      {itemCount > 0 ? (
        <OrderActionBar
          summaryLabel={`${itemCount} × ${t("order.cart.title")}`}
          summaryValue={formatGrosze(subtotal)}
        >
          <Button
            onClick={() => navigate(paths.cart)}
            label={t("order.button.viewCart")}
            variant="contained"
            color="primary"
            maxWidth
            mobile={true}
          />
        </OrderActionBar>
      ) : null}
    </>
  );
}

export default OrderMenuTypePage;
